import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, TrainFront, ChevronDown, ChevronUp } from 'lucide-react';

// Simple car list component
const CarList = ({ cars }) => {
  if (!cars || cars.length === 0) return null;
  
  return (
    <div className="mt-2 space-y-1 pl-5">
      {cars.map((car, idx) => (
        <div key={car.car_display || `car-${idx}`} className="text-sm text-[#5A5A5A] flex items-center gap-2">
          <span className="font-medium text-[#323033]">{car.car_display}</span>
          {car.sr_number && (
            <span className="text-xs text-[#005EF8]">({car.sr_number})</span>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * RepairSiteCard - Displays a single repair site's work schedule summary
 */
const RepairSiteCard = ({ site, index, showFacility }) => {
  const [expanded, setExpanded] = useState(false);
  const carCount = site.car_count || 0;
  const carLabel = carCount === 1 ? 'car' : 'cars';

  return (
    <Card 
      className="border-l-4 border-l-[#005EF8] hover:shadow-md transition-shadow"
      data-testid={`repair-site-card-${index}`}
    >
      <CardContent className="p-4">
        {/* Repair Site Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#005EF8]" />
            <h3 className="font-semibold text-[#323033] text-base">
              {site.repair_site}
            </h3>
          </div>
          <Badge variant="secondary" className="text-xs">
            {carCount} {carLabel}
          </Badge>
        </div>

        {/* Facility (if showing all facilities) */}
        {showFacility && site.facility_name && (
          <p className="text-xs text-[#5A5A5A] mb-2">
            <span className="font-medium">Facility:</span> {site.facility_name}
          </p>
        )}

        {/* Job Lead */}
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-[#5A5A5A]" />
          <span className="text-sm">
            <span className="font-medium text-[#323033]">Lead:</span>{' '}
            <span className="text-[#5A5A5A]">{site.job_lead}</span>
          </span>
        </div>

        {/* Crew */}
        <div className="flex items-start gap-2 mb-3">
          <Users className="w-4 h-4 text-[#5A5A5A] mt-0.5" />
          <span className="text-sm">
            <span className="font-medium text-[#323033]">Crew:</span>{' '}
            <span className="text-[#5A5A5A]">
              {site.crew_display || 'Not specified'}
            </span>
          </span>
        </div>

        {/* Car List (collapsible) */}
        <div className="border-t pt-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-[#005EF8] hover:underline w-full"
            data-testid={`expand-cars-${index}`}
          >
            <TrainFront className="w-4 h-4" />
            <span>View {carCount} {carLabel}</span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 ml-auto" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-auto" />
            )}
          </button>
          
          {expanded && <CarList cars={site.cars} />}
        </div>
      </CardContent>
    </Card>
  );
};

export default RepairSiteCard;
